/**
 * Employees view — lightweight W-2 staff + hourly timekeeping.
 *
 * The money side stays in Expenses: "Record payment" sums an employee's unpaid
 * hours x rate and creates a single Wages expense (deductible, Schedule C
 * Line 26), then marks those hours paid. So payroll flows into the P&L, cash on
 * hand, and tax bundle automatically — no separate financial silo.
 *
 * This is a tracker, not a payroll processor: no withholding, net pay, or W-2
 * generation (leave that to a payroll service / your accountant).
 */
import { DB } from "../lib/db";
import { escapeHtml, toast, $ } from "../lib/dom";
import { fmt$, fmtN, fmtDate, sum, kpiBar, todayISO } from "../utils/format";
import { openModal, closeModal } from "../components/modal";
import { formField, readForm } from "../components/formField";
import { nextId } from "../utils/ids";
import { hashPortalPassword } from "../lib/portalPassword";
import { render } from "../state";
import type { Row } from "../lib/types";

const PAY_METHODS = ["Cash", "ACH", "Check", "Venmo", "PayPal", "Other"];

function empById(id: string): Row | null { return DB.table("employees").find(e => e["Employee ID"] === id) || null; }
function entriesForEmp(id: string): Row[] { return DB.table("timeentries").filter(t => t["Employee ID"] === id); }
function isApproved(t: Row): boolean { return t["Approved"] !== false; } // legacy rows without the flag count as approved
function unpaidEntriesForEmp(id: string): Row[] { return entriesForEmp(id).filter(t => !t["Paid"] && isApproved(t)); }
function pendingEntries(): Row[] { return DB.table("timeentries").filter(t => !isApproved(t) && !t["Paid"]); }
function entryByUuid(uuid: string): Row | null { return DB.table("timeentries").find(t => t && t._uuid === uuid) || null; }
function inThisYear(d: any): boolean { return String(d || "").slice(0, 4) === String(new Date().getFullYear()); }

export function viewEmployees(root: HTMLElement): void {
  const emps = DB.table("employees").slice().sort((a, b) => String(a["Name"] || "").localeCompare(String(b["Name"] || "")));
  const entries = DB.table("timeentries").slice().sort((a, b) => String(b["Date"] || "").localeCompare(String(a["Date"] || "")));
  const activeCount = emps.filter(e => String(e["Status"] || "Active") === "Active").length;
  const unpaidHrs = sum(entries.filter(t => !t["Paid"] && isApproved(t)), t => t["Hours"]);
  const pending = pendingEntries();
  const pendingHrs = sum(pending, t => t["Hours"]);
  const wagesYTD = sum(DB.table("expenses").filter(e => e["Category"] === "Wages" && inThisYear(e["Date"])), e => e["Amount"]);

  // Aggregate time entries into one row per (Employee, YYYY-MM). The Time Cards
  // panel shows these summaries; double-click / Open jumps into the calendar
  // grid for that specific employee + month.
  type CardSummary = { empId: string; emp: string; year: number; month: number; totalHrs: number; paidHrs: number; unpaidHrs: number; pendingHrs: number; rate: number; };
  const cardMap = new Map<string, CardSummary>();
  for (const t of entries) {
    const empId = String(t["Employee ID"] || "");
    const date  = String(t["Date"] || "");
    if (!empId || !date) continue;
    const ym = date.slice(0, 7);
    if (!/^\d{4}-\d{2}$/.test(ym)) continue;
    const key = `${empId}|${ym}`;
    const hrs = Number(t["Hours"]) || 0;
    const paid = !!t["Paid"];
    let cur = cardMap.get(key);
    if (!cur) {
      const [y, m] = ym.split("-").map(Number);
      const emp = empById(empId);
      cur = {
        empId,
        emp:  emp ? (emp["Name"] || empId) : (String(t["Employee"] || "") || empId),
        year: y, month: m,
        totalHrs: 0, paidHrs: 0, unpaidHrs: 0, pendingHrs: 0,
        rate: emp ? (Number(emp["Pay Rate"]) || 0) : 0,
      };
      cardMap.set(key, cur);
    }
    cur.totalHrs += hrs;
    if (paid) cur.paidHrs += hrs;
    else if (isApproved(t)) cur.unpaidHrs += hrs;
    else cur.pendingHrs += hrs;
  }
  const cards = Array.from(cardMap.values()).sort((a, b) => {
    if (a.year !== b.year) return b.year - a.year;
    if (a.month !== b.month) return b.month - a.month;
    return a.emp.localeCompare(b.emp);
  });

  root.innerHTML = `
    ${kpiBar([
      { label: "Employees",      value: `${fmtN(activeCount)} active` },
      { label: "Unpaid hours",   value: fmtN(unpaidHrs) },
      ...(pending.length ? [{ label: "Pending review", value: `${fmtN(pendingHrs)} hrs` }] : []),
      { label: "Wages paid YTD", value: fmt$(wagesYTD) },
      { label: "On payroll",     value: fmtN(emps.length) },
    ])}

    <div class="panel scrollx mb-4">
      <table class="t">
        <thead><tr>
          <th>Employee</th><th>Role</th><th class="text-right">Rate</th><th>Status</th>
          <th class="text-right">Unpaid hrs</th><th class="text-right">Unpaid $</th><th></th>
        </tr></thead>
        <tbody>
          ${emps.map(e => {
            const id = escapeHtml(e["Employee ID"] || "");
            const rate = Number(e["Pay Rate"]) || 0;
            const uHrs = sum(unpaidEntriesForEmp(e["Employee ID"]), t => t["Hours"]);
            const uDol = +(uHrs * rate).toFixed(2);
            const inactive = String(e["Status"] || "Active") !== "Active";
            return `<tr class="${inactive ? "opacity-60" : ""}">
              <td><b>${escapeHtml(e["Name"] || "")}</b> <span class="font-mono text-xs text-[var(--muted)]">${id}</span></td>
              <td>${escapeHtml(e["Role"] || "")}</td>
              <td class="text-right stat-num">${fmt$(rate)}/hr</td>
              <td>${escapeHtml(e["Status"] || "Active")}</td>
              <td class="text-right stat-num">${fmtN(uHrs)}</td>
              <td class="text-right stat-num text-amber-300">${fmt$(uDol)}</td>
              <td class="text-right whitespace-nowrap">
                <button class="btn btn-ghost !py-0.5 !px-2 text-xs" onclick="logTime('${id}')" title="Log hours">＋ Hrs</button>
                <button class="btn btn-primary !py-0.5 !px-2 text-xs ${uHrs > 0 ? "" : "opacity-50"}" onclick="recordPayment('${id}')" title="Record payment">$ Pay</button>
                <button class="btn btn-ghost !py-0.5 !px-2 text-xs" onclick="editEmployee('${id}')" title="Edit">✎</button>
              </td>
            </tr>`;
          }).join("") || `<tr><td colspan="7" class="text-center text-[var(--muted)] py-8">No employees yet. Click "+ Add Employee".</td></tr>`}
        </tbody>
      </table>
    </div>

    ${pending.length ? `
    <div class="panel scrollx mb-4">
      <div class="px-3 pt-3 pb-2 flex flex-wrap items-center gap-2 border-b border-[var(--border)]">
        <div class="text-sm font-semibold text-amber-300">Pending Approval</div>
        <div class="text-xs text-[var(--muted)]">${pending.length} entr${pending.length === 1 ? "y" : "ies"} submitted from the time portal</div>
        <button class="btn btn-primary !py-1 !px-3 text-xs ml-auto" onclick="approveAllPending()">Approve all</button>
      </div>
      <table class="t">
        <thead><tr><th>Date</th><th>Employee</th><th class="text-right">Hours</th><th>Task</th><th></th></tr></thead>
        <tbody>
          ${pending.slice().sort((a, b) => String(a["Date"] || "").localeCompare(String(b["Date"] || ""))).map(t => `<tr>
            <td>${fmtDate(t["Date"])}</td>
            <td>${escapeHtml(t["Employee"] || t["Employee ID"] || "")}</td>
            <td class="text-right stat-num">${fmtN(t["Hours"])}</td>
            <td class="text-xs text-[var(--muted)]">${escapeHtml(t["Task"] || "")}</td>
            <td class="text-right whitespace-nowrap">
              <button class="btn btn-primary !py-0.5 !px-2 text-xs" onclick="approveTimeEntry('${escapeHtml(t._uuid || "")}')">Approve</button>
              <button class="btn btn-ghost !py-0.5 !px-2 text-xs ml-1" onclick="editTime('${escapeHtml(t._uuid || "")}')">Edit</button>
              <button class="btn btn-ghost !py-0.5 !px-2 text-xs ml-1 !text-rose-300" onclick="rejectTimeEntry('${escapeHtml(t._uuid || "")}')">Reject</button>
            </td>
          </tr>`).join("")}
        </tbody>
      </table>
    </div>` : ""}

    <div class="panel scrollx">
      <div class="px-3 pt-3 pb-2 flex flex-wrap items-center gap-2 border-b border-[var(--border)]">
        <div class="text-sm font-semibold text-[var(--muted)]">Time Cards</div>
        <div class="flex gap-2 ml-auto">
          <button class="btn btn-ghost !py-1 !px-3 text-xs" onclick="logTime()" title="Log a single day of hours">+ Log Hours</button>
          <button class="btn btn-primary !py-1 !px-3 text-xs" onclick="openMonthlyTimeCard()" title="Fill in hours for a whole month at once">+ Time Card</button>
        </div>
      </div>
      <table class="t">
        <thead><tr>
          <th>Month</th><th>Employee</th><th class="text-right">Hours</th><th class="text-right">Unpaid hrs</th><th class="text-right">Unpaid $</th><th>Status</th><th></th>
        </tr></thead>
        <tbody>
          ${cards.map(c => {
            const monthLabel = new Date(c.year, c.month - 1, 1).toLocaleDateString(undefined, { month: "long", year: "numeric" });
            const fullyPaid = c.unpaidHrs < 0.001 && c.pendingHrs < 0.001 && c.totalHrs > 0;
            const partial   = c.paidHrs > 0.001 && c.unpaidHrs > 0.001;
            const allUnpaid = c.paidHrs < 0.001 && c.totalHrs > 0;
            const pendingPill = c.pendingHrs > 0.001 ? ` <span class="pill pill-amber text-xs" title="Employee-submitted hours awaiting your approval">${fmtN(c.pendingHrs)} pending</span>` : "";
            const statusHtml = (fullyPaid ? `<span class="pill pill-blue text-xs">Paid</span>`
                             : partial   ? `<span class="pill pill-amber text-xs">Partial</span>`
                             : allUnpaid ? `<span class="pill pill-amber text-xs">Unpaid</span>`
                             :             `<span class="text-[var(--muted)]">&mdash;</span>`) + pendingPill;
            const unpaidDollars = +(c.unpaidHrs * c.rate).toFixed(2);
            return `<tr class="cursor-pointer hover:bg-[var(--panel-2)]" ondblclick="openMonthlyTimeCard('${escapeHtml(c.empId)}', ${c.year}, ${c.month})" title="Double-click to open">
              <td><b>${escapeHtml(monthLabel)}</b></td>
              <td>${escapeHtml(c.emp)}</td>
              <td class="text-right stat-num">${fmtN(c.totalHrs)}</td>
              <td class="text-right stat-num">${c.unpaidHrs > 0 ? fmtN(c.unpaidHrs) : '<span class="text-[var(--muted)]">&mdash;</span>'}</td>
              <td class="text-right stat-num ${unpaidDollars > 0 ? "text-amber-300" : "text-[var(--muted)]"}">${unpaidDollars > 0 ? fmt$(unpaidDollars) : "&mdash;"}</td>
              <td>${statusHtml}</td>
              <td class="text-right whitespace-nowrap">
                <button class="btn btn-ghost !py-0.5 !px-2 text-xs" onclick="event.stopPropagation(); openMonthlyTimeCard('${escapeHtml(c.empId)}', ${c.year}, ${c.month})">Open</button>
                ${c.unpaidHrs > 0.001 ? `<button class="btn btn-primary !py-0.5 !px-2 text-xs ml-1" onclick="event.stopPropagation(); recordMonthlyPayment('${escapeHtml(c.empId)}', ${c.year}, ${c.month})" title="Pay just this month">$ Pay</button>` : ""}
                ${c.paidHrs > 0.001 ? `<button class="btn btn-ghost !py-0.5 !px-2 text-xs ml-1" onclick="event.stopPropagation(); reopenTimeCard('${escapeHtml(c.empId)}', ${c.year}, ${c.month})" title="Reopen for editing - removes this month's Wages expense and marks the hours unpaid">Reopen</button>` : ""}
                <button class="btn btn-ghost !py-0.5 !px-2 text-xs ml-1 !text-rose-300" onclick="event.stopPropagation(); deleteTimeCard('${escapeHtml(c.empId)}', ${c.year}, ${c.month})" title="Delete every entry for this month">Delete</button>
              </td>
            </tr>`;
          }).join("") || `<tr><td colspan="7" class="text-center text-[var(--muted)] py-8">No time cards yet. Click "+ Time Card" to start one for this month.</td></tr>`}
        </tbody>
      </table>
    </div>
  `;
}

/* ---------------- Employee form ---------------- */
export function editEmployee(id?: string): void {
  const isNew = !id;
  const row: Row = isNew
    ? { "Employee ID": nextId("EMP", "employees", "Employee ID"), "Status": "Active", "Start Date": todayISO() }
    : (empById(id!) || {});
  if (!isNew && !row["Employee ID"]) { toast("Employee not found", "err"); return; }

  const body = `
    <form id="employeeForm" class="grid grid-cols-1 sm:grid-cols-2 gap-3" onsubmit="return false;">
      ${formField({ label: "Employee ID", name: "Employee ID", value: row["Employee ID"] })}
      ${formField({ label: "Name", name: "Name", value: row["Name"], required: true })}
      ${formField({ label: "Role", name: "Role", value: row["Role"], placeholder: "e.g. Sorting / shipping" })}
      ${formField({ label: "Pay rate ($/hr)", name: "Pay Rate", type: "number", step: "0.01", value: row["Pay Rate"] })}
      ${formField({ label: "Status", name: "Status", value: row["Status"] || "Active", options: ["Active", "Inactive"] })}
      ${formField({ label: "Start date", name: "Start Date", type: "date", value: fmtDate(row["Start Date"]) })}
      <div class="sm:col-span-2">${formField({ label: "Notes", name: "Notes", type: "textarea", value: row["Notes"] })}</div>
      <div class="sm:col-span-2 panel-2 p-2">
        <label class="label">Time-portal password <span class="text-[var(--muted)] font-normal">${row["Password Hash"] ? "(set — type a new one to change, or leave blank to keep)" : "(not set — employee can\u2019t sign in yet)"}</span></label>
        <input class="input" type="text" id="empPortalPw" autocomplete="off"
               placeholder="${row["Password Hash"] ? "••••••••" : "Choose a password for this employee"}">
        <div class="text-[11px] text-[var(--muted)] mt-1">Employees sign in at <b>/#time-portal</b> with their name + this password to submit hours for your approval.</div>
      </div>
    </form>`;
  const safe = escapeHtml(row["Employee ID"] || "");
  const footer = `
    ${!isNew ? `<button class="btn btn-danger" onclick="deleteEmployee('${safe}')">Delete</button>` : ""}
    <button class="btn btn-ghost" onclick="closeModal()">Cancel</button>
    <button class="btn btn-primary" onclick="saveEmployee('${isNew ? "" : safe}')">${isNew ? "Add" : "Save"}</button>`;
  openModal({ title: isNew ? "Add Employee" : "Edit Employee", body, footer });
}

export async function saveEmployee(id: string): Promise<void> {
  const d = readForm($("#employeeForm"));
  if (!d["Name"] || !String(d["Name"]).trim()) { toast("Name is required", "err"); return; }
  d["Pay Rate"] = Number(d["Pay Rate"]) || 0;
  const pwEl = document.getElementById("empPortalPw") as HTMLInputElement | null;
  const newPw = (pwEl?.value || "").trim();
  if (newPw) {
    if (newPw.length < 6) { toast("Portal password must be at least 6 characters", "err"); return; }
    d["Password Hash"] = await hashPortalPassword(newPw);
  }
  try {
    if (!id) await DB.add("employees", d);
    else     DB.update("employees", "Employee ID", id, d);
  } catch (e: any) { toast("Cloud sync failed: " + (e?.message || e), "err"); return; }
  toast("Employee saved");
  closeModal();
  render();
}

export async function deleteEmployee(id: string): Promise<void> {
  if (!id) return;
  if (!confirm("Delete this employee? Their logged time entries stay on file.")) return;
  try { await DB.remove("employees", "Employee ID", id); }
  catch (e: any) { toast("Cloud delete failed: " + (e?.message || e), "err"); return; }
  toast("Deleted");
  closeModal();
  render();
}

/* ---------------- Time entry form ---------------- */
export function logTime(preEmpId?: string): void {
  timeForm(undefined, preEmpId);
}
export function editTime(uuid: string): void {
  timeForm(uuid);
}
function timeForm(uuid?: string, preEmpId?: string): void {
  const existing = uuid ? entryByUuid(uuid) : null;
  if (uuid && !existing) { toast("Entry not found", "err"); return; }
  const emps = DB.table("employees").slice().sort((a, b) => String(a["Name"] || "").localeCompare(String(b["Name"] || "")));
  const selId = existing ? existing["Employee ID"] : (preEmpId || "");
  const body = `
    <form id="timeForm" class="grid grid-cols-1 sm:grid-cols-2 gap-3" onsubmit="return false;">
      <div>
        <label class="label">Employee</label>
        <select class="input" name="Employee ID" required>
          <option value="">— pick —</option>
          ${emps.map(e => `<option value="${escapeHtml(e["Employee ID"])}" ${e["Employee ID"] === selId ? "selected" : ""}>${escapeHtml(e["Name"] || e["Employee ID"])}</option>`).join("")}
        </select>
      </div>
      ${formField({ label: "Date", name: "Date", type: "date", value: fmtDate(existing ? existing["Date"] : todayISO()), required: true })}
      ${formField({ label: "Hours", name: "Hours", type: "number", step: "0.25", value: existing ? existing["Hours"] : "" })}
      ${formField({ label: "Task", name: "Task", value: existing ? existing["Task"] : "", placeholder: "e.g. Sorting bulk, packing orders" })}
    </form>
    ${existing && existing["Paid"] ? `<div class="text-xs text-amber-300 mt-2">These hours are already marked paid — editing won't change the recorded Wages expense.</div>` : ""}
    ${existing && !existing["Paid"] && existing["Approved"] === false ? `<div class="text-xs text-amber-300 mt-2">Submitted by the employee from the time portal — saving approves it.</div>` : ""}`;
  const u = uuid ? escapeHtml(uuid) : "";
  const footer = `
    <button class="btn btn-ghost" onclick="closeModal()">Cancel</button>
    <button class="btn btn-primary" onclick="saveTime('${u}')">${uuid ? "Save" : "Add hours"}</button>`;
  openModal({ title: uuid ? "Edit Hours" : "Log Hours", body, footer });
}

export async function saveTime(uuid: string): Promise<void> {
  const d = readForm($("#timeForm"));
  const empId = d["Employee ID"];
  if (!empId) { toast("Pick an employee", "err"); return; }
  const emp = empById(empId);
  const existing = uuid ? entryByUuid(uuid) : null;
  const row: Row = {
    "Employee ID": empId,
    "Employee": emp ? emp["Name"] : (existing ? existing["Employee"] : ""),
    "Date": d["Date"] || todayISO(),
    "Hours": Number(d["Hours"]) || 0,
    "Task": d["Task"] || "",
    "Paid": existing ? !!existing["Paid"] : false,
    "Paid Date": existing ? (existing["Paid Date"] || "") : "",
    "Expense Ref": existing ? (existing["Expense Ref"] || "") : "",
    "Approved": true, // owner-entered or owner-reviewed = approved
    "Entered By": existing ? (existing["Entered By"] || "owner") : "owner",
  };
  try {
    if (!uuid) await DB.add("timeentries", row);
    else       await DB.updateByUuid("timeentries", uuid, row);
  } catch (e: any) { toast("Cloud sync failed: " + (e?.message || e), "err"); return; }
  toast("Hours saved");
  closeModal();
  render();
}

export async function deleteTime(uuid: string): Promise<void> {
  if (!uuid) return;
  if (!confirm("Delete this time entry?")) return;
  try { await DB.removeByUuid("timeentries", uuid); }
  catch (e: any) { toast("Cloud delete failed: " + (e?.message || e), "err"); return; }
  toast("Deleted");
  render();
}

/* ---------------- Record payment -> Wages expense ----------------
   Selective payment: tick any set of unpaid (approved) days - a single
   day, a week, a month, or everything. One deductible Wages expense is
   booked PER MONTH covered by the selection, so the existing per-month
   reopen/delete mechanics keep working; a partially paid month simply
   shows Partial and can be topped up with another payment later.
------------------------------------------------------------------- */
interface PayState {
  empId: string;
  selected: Set<string>;  // _uuid's of the entries to pay
  entries: Row[];         // unpaid approved entries, date-sorted
}
let _payState: PayState | null = null;

/** Sunday that starts the week containing dateStr (local, YYYY-MM-DD). */
function weekStartStr(dateStr: string): string {
  const [y, m, d] = String(dateStr).split("-").map(Number);
  const dt = new Date(y, (m || 1) - 1, d || 1);
  dt.setDate(dt.getDate() - dt.getDay());
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}`;
}

export function recordPayment(empId: string): void {
  const emp = empById(empId);
  if (!emp) { toast("Employee not found", "err"); return; }
  const rate = Number(emp["Pay Rate"]) || 0;
  if (rate <= 0) { toast("Set this employee's pay rate first.", "err"); return; }
  const unpaid = unpaidEntriesForEmp(empId)
    .filter(t => t._uuid && /^\d{4}-\d{2}/.test(String(t["Date"] || "")))
    .sort((a, b) => String(a["Date"]).localeCompare(String(b["Date"])));
  if (!unpaid.length) { toast(`No approved unpaid hours for ${emp["Name"]}.`, "err"); return; }

  _payState = { empId, selected: new Set(unpaid.map(t => String(t._uuid))), entries: unpaid };

  const body = `
    <div class="space-y-3">
      <div class="panel-2 p-3 text-sm flex flex-wrap items-baseline gap-2">
        <span>Paying <b>${escapeHtml(emp["Name"] || "")}</b> at <b>${fmt$(rate)}/hr</b>.</span>
        <span class="text-xs text-[var(--muted)]">Tick what to include — a day, a week, a month, or everything.</span>
        <span class="ml-auto flex gap-2">
          <button class="btn btn-ghost !py-0.5 !px-2 text-xs" onclick="paySelectAll(true)">All</button>
          <button class="btn btn-ghost !py-0.5 !px-2 text-xs" onclick="paySelectAll(false)">None</button>
        </span>
      </div>
      <div id="payList" class="max-h-[45vh] overflow-y-auto pr-1">${payListHtml(rate)}</div>
      <div class="flex items-baseline justify-between pt-2 border-t border-[var(--border)]">
        <span class="text-sm uppercase tracking-wider text-[var(--muted)]">Selected</span>
        <span id="payTotal" class="stat-num text-xl font-bold text-emerald-300"></span>
      </div>
      <form id="payForm" class="grid grid-cols-1 sm:grid-cols-2 gap-3" onsubmit="return false;">
        ${formField({ label: "Payment date", name: "Pay Date", type: "date", value: todayISO(), required: true })}
        ${formField({ label: "Payment method", name: "Pay Method", value: "Cash", options: PAY_METHODS })}
      </form>
      <div class="text-xs text-[var(--muted)]">One deductible <b>Wages</b> expense is created per month covered by the selection. FICA-exempt for under-18 child wages in an SMLLC; confirm filing with your accountant.</div>
    </div>`;
  const footer = `
    <button class="btn btn-ghost" onclick="closeModal()">Cancel</button>
    <button class="btn btn-primary" id="payConfirmBtn" onclick="confirmPayment('${escapeHtml(empId)}')"></button>`;
  openModal({ title: "Record Payment", body, footer, wide: true });
  payRefresh();
}

/** Month > week > day checkbox tree for the unpaid approved entries. */
function payListHtml(rate: number): string {
  const s = _payState; if (!s) return "";
  const months = new Map<string, Map<string, Row[]>>();
  for (const t of s.entries) {
    const ds = String(t["Date"]);
    const ym = ds.slice(0, 7);
    const wk = weekStartStr(ds);
    let m = months.get(ym); if (!m) { m = new Map(); months.set(ym, m); }
    let w = m.get(wk); if (!w) { w = []; m.set(wk, w); }
    w.push(t);
  }
  const out: string[] = [];
  for (const [ym, weeks] of Array.from(months.entries()).sort()) {
    const [y, mo] = ym.split("-").map(Number);
    const monthLabel = new Date(y, mo - 1, 1).toLocaleDateString(undefined, { month: "long", year: "numeric" });
    let mHrs = 0;
    for (const arr of weeks.values()) for (const t of arr) mHrs += Number(t["Hours"]) || 0;
    out.push(`
      <label class="flex items-baseline gap-2 py-1 font-semibold cursor-pointer border-t border-[var(--border)] first:border-t-0">
        <input type="checkbox" data-paymonth="${ym}" onchange="payToggleGroup('month','${ym}',this.checked)">
        <span>${escapeHtml(monthLabel)}</span>
        <span class="ml-auto text-xs text-[var(--muted)] font-normal"><b class="stat-num text-[var(--text)]">${fmtN(mHrs)}</b> hrs · ${fmt$(+(mHrs * rate).toFixed(2))}</span>
      </label>`);
    for (const [wk, entries] of Array.from(weeks.entries()).sort()) {
      const wHrs = entries.reduce((a, t) => a + (Number(t["Hours"]) || 0), 0);
      const [wy, wm, wd] = wk.split("-").map(Number);
      const wkLabel = new Date(wy, wm - 1, wd).toLocaleDateString(undefined, { month: "short", day: "numeric" });
      out.push(`
        <label class="flex items-baseline gap-2 py-0.5 pl-5 text-sm cursor-pointer">
          <input type="checkbox" data-payweek="${wk}" onchange="payToggleGroup('week','${wk}',this.checked)">
          <span>Week of ${escapeHtml(wkLabel)}</span>
          <span class="ml-auto text-xs text-[var(--muted)]">${fmtN(wHrs)} hrs · ${fmt$(+(wHrs * rate).toFixed(2))}</span>
        </label>`);
      for (const t of entries) {
        const uuid = String(t._uuid);
        const ds = String(t["Date"]);
        const [dy, dm, dd] = ds.split("-").map(Number);
        const dayLabel = new Date(dy, dm - 1, dd).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
        out.push(`
          <label class="flex items-baseline gap-2 py-0.5 pl-10 text-sm cursor-pointer">
            <input type="checkbox" data-payday="${uuid}" onchange="payToggleDay('${uuid}',this.checked)">
            <span>${escapeHtml(dayLabel)}</span>
            ${t["Task"] ? `<span class="text-xs text-[var(--muted)] truncate max-w-[180px]">· ${escapeHtml(t["Task"])}</span>` : ""}
            <span class="ml-auto text-xs stat-num">${fmtN(t["Hours"])} hrs</span>
          </label>`);
      }
    }
  }
  return out.join("");
}

/** Sync checkbox states (incl. indeterminate groups), total, and button label. */
function payRefresh(): void {
  const s = _payState; if (!s) return;
  const emp = empById(s.empId);
  const rate = emp ? (Number(emp["Pay Rate"]) || 0) : 0;
  const byUuid = new Map(s.entries.map(t => [String(t._uuid), t]));
  document.querySelectorAll<HTMLInputElement>("input[data-payday]").forEach(cb => {
    cb.checked = s.selected.has(cb.dataset.payday || "");
  });
  document.querySelectorAll<HTMLInputElement>("input[data-paymonth], input[data-payweek]").forEach(cb => {
    const isMonth = cb.dataset.paymonth !== undefined;
    const key = (isMonth ? cb.dataset.paymonth : cb.dataset.payweek) || "";
    const members = s.entries.filter(t => isMonth
      ? String(t["Date"]).slice(0, 7) === key
      : weekStartStr(String(t["Date"])) === key);
    const sel = members.filter(t => s.selected.has(String(t._uuid))).length;
    cb.checked = members.length > 0 && sel === members.length;
    cb.indeterminate = sel > 0 && sel < members.length;
  });
  let hrs = 0;
  for (const u of Array.from(s.selected)) { const t = byUuid.get(u); if (t) hrs += Number(t["Hours"]) || 0; }
  const gross = +(hrs * rate).toFixed(2);
  const totalEl = document.getElementById("payTotal");
  if (totalEl) totalEl.textContent = `${fmtN(hrs)} hrs · ${fmt$(gross)}`;
  const btn = document.getElementById("payConfirmBtn") as HTMLButtonElement | null;
  if (btn) {
    btn.textContent = s.selected.size ? `Record ${fmt$(gross)} payment` : "Nothing selected";
    btn.classList.toggle("opacity-50", !s.selected.size);
  }
}

export function payToggleDay(uuid: string, checked: boolean): void {
  const s = _payState; if (!s) return;
  if (checked) s.selected.add(uuid); else s.selected.delete(uuid);
  payRefresh();
}

export function payToggleGroup(kind: "month" | "week", key: string, checked: boolean): void {
  const s = _payState; if (!s) return;
  for (const t of s.entries) {
    const ds = String(t["Date"]);
    const match = kind === "month" ? ds.slice(0, 7) === key : weekStartStr(ds) === key;
    if (!match) continue;
    const u = String(t._uuid);
    if (checked) s.selected.add(u); else s.selected.delete(u);
  }
  payRefresh();
}

export function paySelectAll(checked: boolean): void {
  const s = _payState; if (!s) return;
  s.selected = checked ? new Set(s.entries.map(t => String(t._uuid))) : new Set();
  payRefresh();
}

export async function confirmPayment(empId: string): Promise<void> {
  const emp = empById(empId);
  if (!emp) { toast("Employee not found", "err"); return; }
  const rate = Number(emp["Pay Rate"]) || 0;
  if (rate <= 0) { toast("Set this employee's pay rate first.", "err"); return; }
  // Re-read from LIVE data so anything paid/edited since the modal opened
  // can't be double-paid: only still-unpaid approved entries that are selected.
  const st = _payState;
  const live = unpaidEntriesForEmp(empId);
  const chosen = st ? live.filter(t => t._uuid && st.selected.has(String(t._uuid))) : live;
  if (!chosen.length) { toast("Nothing selected to pay.", "err"); return; }

  const d = readForm($("#payForm"));
  const payDate = d["Pay Date"] || todayISO();
  const method = d["Pay Method"] || "Cash";

  // Group the selection by month and book one Wages expense per month.
  const byMonth = new Map<string, Row[]>();
  for (const t of chosen) {
    const ym = String(t["Date"] || "").slice(0, 7);
    if (!/^\d{4}-\d{2}$/.test(ym)) continue;
    let arr = byMonth.get(ym);
    if (!arr) { arr = []; byMonth.set(ym, arr); }
    arr.push(t);
  }

  let expensesBooked = 0, totalGross = 0;
  try {
    for (const [ym, entries] of byMonth) {
      const monthHrs = entries.reduce((a, t) => a + (Number(t["Hours"]) || 0), 0);
      const monthGross = +(monthHrs * rate).toFixed(2);
      if (monthGross <= 0) continue;
      const [y, m] = ym.split("-").map(Number);
      const monthLabel = new Date(y, m - 1, 1).toLocaleDateString(undefined, { month: "short", year: "numeric" });
      const payRef = `PAY-${String(new Date().getFullYear()).slice(2)}-${ym.replace("-", "")}-${String(Date.now()).slice(-5)}`;

      await DB.add("expenses", {
        "Date":           payDate,
        "Category":       "Wages",
        "Vendor":         emp["Name"] || emp["Employee ID"],
        "Amount":         monthGross,
        "Deductible":     "Yes",
        "Payment Method": method,
        "Notes":          `Wages: ${fmtN(monthHrs)} hrs @ ${fmt$(rate)}/hr for ${emp["Name"]} (${monthLabel}) · ${payRef}`,
      });
      for (const t of entries) {
        if (!t._uuid) continue;
        await DB.updateByUuid("timeentries", t._uuid, { ...t, "Paid": true, "Paid Date": payDate, "Expense Ref": payRef });
      }
      expensesBooked++;
      totalGross += monthGross;
    }
  } catch (e: any) { toast("Cloud sync failed: " + (e?.message || e), "err"); return; }

  _payState = null;
  toast(`Recorded ${fmt$(totalGross)} for ${emp["Name"]} across ${expensesBooked} month${expensesBooked === 1 ? "" : "s"}`);
  closeModal();
  render();
}

/* ============================================================
   Monthly Time Card — fill in hours for a whole month at once
   ============================================================ */
interface MtcState {
  empId: string;
  year: number;
  month: number;  // 1-12
  hours: Record<number, number>;       // day -> live hours from inputs
  originalHours: Record<number, number>; // day -> total hours summed from existing entries
  paidDays: Set<number>;               // days that have at least one paid entry (locked)
  submittedDays: Set<number>;          // days with employee-submitted (unapproved) entries
  task: string;                        // default task applied to newly-added entries
}
let _mtcState: MtcState | null = null;

export function openMonthlyTimeCard(preEmpId?: string, year?: number, month?: number): void {
  const emps = DB.table("employees").slice().sort((a, b) => String(a["Name"] || "").localeCompare(String(b["Name"] || "")));
  if (!emps.length) { toast("Add an employee first.", "err"); return; }
  const initialEmpId = preEmpId || emps[0]["Employee ID"];

  // If year/month aren't specified, auto-advance to the next month after the
  // latest existing entry across all employees (so + Time Card walks forward
  // consecutively). Falls back to the current month when no entries exist.
  let initialYear = year, initialMonth = month;
  if (!initialYear || !initialMonth) {
    let latestY = 0, latestM = 0;
    for (const t of DB.table("timeentries")) {
      const d = String(t["Date"] || "");
      const y = Number(d.slice(0, 4)) || 0;
      const m = Number(d.slice(5, 7)) || 0;
      if (y > latestY || (y === latestY && m > latestM)) { latestY = y; latestM = m; }
    }
    if (latestY) {
      initialYear  = latestM === 12 ? latestY + 1 : latestY;
      initialMonth = latestM === 12 ? 1 : latestM + 1;
    } else {
      const now = new Date();
      initialYear  = now.getFullYear();
      initialMonth = now.getMonth() + 1;
    }
  }

  _mtcState = {
    empId: initialEmpId,
    year:  initialYear,
    month: initialMonth,
    hours: {},
    originalHours: {},
    paidDays: new Set(),
    submittedDays: new Set(),
    task: "",
  };
  _loadMtcEntries();
  _renderMtcModal();
}

function _loadMtcEntries(): void {
  const s = _mtcState; if (!s) return;
  const prefix = `${s.year}-${String(s.month).padStart(2, "0")}`;
  const entries = DB.table("timeentries").filter(t =>
    t["Employee ID"] === s.empId &&
    String(t["Date"] || "").startsWith(prefix)
  );
  s.originalHours = {};
  s.hours = {};
  s.paidDays = new Set();
  s.submittedDays = new Set();
  for (const t of entries) {
    const day = Number(String(t["Date"] || "").slice(8, 10));
    if (!day) continue;
    s.originalHours[day] = (s.originalHours[day] || 0) + (Number(t["Hours"]) || 0);
    if (t["Paid"]) s.paidDays.add(day);
    else if (t["Approved"] === false) s.submittedDays.add(day);
  }
  // Seed live hours from originals so the inputs pre-populate.
  s.hours = { ...s.originalHours };
}

function _renderMtcModal(): void {
  const s = _mtcState; if (!s) return;
  const emps = DB.table("employees").slice().sort((a, b) => String(a["Name"] || "").localeCompare(String(b["Name"] || "")));
  const monthDate = new Date(s.year, s.month - 1, 1);
  const monthLabel = monthDate.toLocaleDateString(undefined, { month: "long", year: "numeric" });
  const daysInMonth = new Date(s.year, s.month, 0).getDate();
  const firstWeekday = new Date(s.year, s.month - 1, 1).getDay(); // 0 = Sun

  const cells: string[] = [];
  for (let i = 0; i < firstWeekday; i++) cells.push('<div class="opacity-0 pointer-events-none">.</div>');
  for (let d = 1; d <= daysInMonth; d++) {
    const hrs = s.hours[d] || 0;
    const isPaid = s.paidDays.has(d);
    const isSubmitted = s.submittedDays.has(d);
    const dow = new Date(s.year, s.month - 1, d).getDay();
    const isWeekend = dow === 0 || dow === 6;
    cells.push(`
      <div class="panel-2 p-1.5 ${isWeekend ? "opacity-80" : ""} ${isPaid ? "ring-1 ring-emerald-500/40" : isSubmitted ? "ring-1 ring-amber-500/40" : ""}">
        <div class="flex items-center justify-between text-[10px] text-[var(--muted)] mb-1">
          <span>${d}</span>${isPaid ? '<span class="text-emerald-300 text-[9px] uppercase tracking-wider">paid</span>' : isSubmitted ? '<span class="text-amber-300 text-[9px] uppercase tracking-wider" title="Employee-submitted, awaiting approval — saving this day approves your value">sent</span>' : ""}
        </div>
        <input class="input !py-0.5 !px-1.5 text-sm stat-num text-right ${isPaid ? "opacity-60 pointer-events-none" : ""}"
               type="number" step="0.25" min="0" max="24"
               value="${hrs > 0 ? hrs : ""}"
               placeholder="0"
               ${isPaid ? "disabled" : ""}
               oninput="updateMtcDay(${d}, this.value)">
      </div>
    `);
  }

  const total = Object.values(s.hours).reduce((a, b) => a + (Number(b) || 0), 0);
  const emp = empById(s.empId);
  const rate = emp ? (Number(emp["Pay Rate"]) || 0) : 0;
  const grossPreview = +(total * rate).toFixed(2);
  const lockedCount = s.paidDays.size;

  const body = `
    <div class="space-y-3">
      <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label class="label">Employee</label>
          <select class="input" onchange="changeMtcEmp(this.value)">
            ${emps.map(e => `<option value="${escapeHtml(e["Employee ID"])}" ${e["Employee ID"] === s.empId ? "selected" : ""}>${escapeHtml(e["Name"] || e["Employee ID"])}</option>`).join("")}
          </select>
        </div>
        <div>
          <label class="label">Month</label>
          <div class="flex items-center gap-2">
            <button class="btn btn-ghost !py-1 !px-2 text-xs" onclick="navMtcMonth(-1)" title="Previous month">‹</button>
            <span class="flex-1 text-center font-semibold">${escapeHtml(monthLabel)}</span>
            <button class="btn btn-ghost !py-1 !px-2 text-xs" onclick="navMtcMonth(1)" title="Next month">›</button>
          </div>
        </div>
      </div>

      <div>
        <label class="label">Default task <span class="text-[var(--muted)] font-normal">(applied to new entries only)</span></label>
        <input class="input text-sm" placeholder="e.g. Sorting bulk, packing orders"
               oninput="updateMtcTask(this.value)" value="${escapeHtml(s.task)}">
      </div>

      <div>
        <div class="grid grid-cols-7 gap-1 mb-1">
          ${["Sun","Mon","Tue","Wed","Thu","Fri","Sat"].map(d =>
            `<div class="text-[10px] uppercase tracking-wider text-[var(--muted)] text-center pb-1">${d}</div>`
          ).join("")}
        </div>
        <div class="grid grid-cols-7 gap-1">${cells.join("")}</div>
        ${lockedCount > 0 ? `<div class="text-xs text-[var(--muted)] mt-2">${lockedCount} day${lockedCount === 1 ? "" : "s"} with paid hours locked - delete the underlying Wages expense first if you need to re-edit those.</div>` : ""}
      </div>

      <div class="flex items-baseline justify-between border-t border-[var(--border)] pt-3">
        <span class="text-sm text-[var(--muted)] uppercase tracking-wider">Month total</span>
        <div class="flex items-baseline gap-3">
          <span id="mtcTotal" class="stat-num text-2xl font-bold">${fmtN(total)} hrs</span>
          ${rate > 0 ? `<span id="mtcGross" class="text-sm text-[var(--muted)]">@ ${fmt$(rate)}/hr = <b class="stat-num text-[var(--text)]">${fmt$(grossPreview)}</b></span>` : ""}
        </div>
      </div>
    </div>
  `;

  const footer = `
    <button class="btn btn-ghost" onclick="closeModal()">Cancel</button>
    <button class="btn btn-primary" onclick="saveMonthlyTimeCard()">Save time card</button>
  `;

  openModal({ title: "Monthly Time Card", body, footer, wide: true });
}

export function updateMtcDay(day: number, value: string): void {
  const s = _mtcState; if (!s) return;
  const hrs = Math.max(0, Math.min(24, Number(value) || 0));
  if (hrs === 0) delete s.hours[day]; else s.hours[day] = hrs;
  // Live update of total + gross preview without re-rendering the whole modal.
  const total = Object.values(s.hours).reduce((a, b) => a + (Number(b) || 0), 0);
  const totalEl = document.getElementById("mtcTotal");
  if (totalEl) totalEl.textContent = `${fmtN(total)} hrs`;
  const grossEl = document.getElementById("mtcGross");
  const emp = empById(s.empId);
  const rate = emp ? (Number(emp["Pay Rate"]) || 0) : 0;
  if (grossEl && rate > 0) {
    grossEl.innerHTML = `@ ${fmt$(rate)}/hr = <b class="stat-num text-[var(--text)]">${fmt$(+(total * rate).toFixed(2))}</b>`;
  }
}

export function updateMtcTask(t: string): void {
  const s = _mtcState; if (!s) return;
  s.task = t;
}

export function changeMtcEmp(empId: string): void {
  const s = _mtcState; if (!s) return;
  s.empId = empId;
  _loadMtcEntries();
  _renderMtcModal();
}

export function navMtcMonth(delta: number): void {
  const s = _mtcState; if (!s) return;
  s.month += delta;
  if (s.month < 1) { s.month = 12; s.year--; }
  if (s.month > 12) { s.month = 1; s.year++; }
  _loadMtcEntries();
  _renderMtcModal();
}

export async function saveMonthlyTimeCard(): Promise<void> {
  const s = _mtcState; if (!s) return;
  const emp = empById(s.empId);
  if (!emp) { toast("Pick an employee", "err"); return; }

  const prefix = `${s.year}-${String(s.month).padStart(2, "0")}`;
  const allExisting = DB.table("timeentries").filter(t =>
    t["Employee ID"] === s.empId &&
    String(t["Date"] || "").startsWith(prefix)
  );
  const byDay: Record<number, Row[]> = {};
  for (const t of allExisting) {
    const day = Number(String(t["Date"] || "").slice(8, 10));
    if (!day) continue;
    (byDay[day] ||= []).push(t);
  }

  const daysInMonth = new Date(s.year, s.month, 0).getDate();
  let added = 0, replaced = 0, deleted = 0, skipped = 0;

  for (let d = 1; d <= daysInMonth; d++) {
    if (s.paidDays.has(d)) { skipped++; continue; }
    const newHrs = s.hours[d] || 0;
    const dayExisting = byDay[d] || [];
    const existingHrs = dayExisting.reduce((a, t) => a + (Number(t["Hours"]) || 0), 0);
    if (Math.abs(newHrs - existingHrs) < 0.001) continue; // no change

    // Delete all existing unpaid entries for this day, then add one consolidated entry.
    for (const t of dayExisting) {
      if (t["Paid"]) continue; // safety - shouldn't happen since paidDays guards
      if (!t._uuid) continue;
      try { await DB.removeByUuid("timeentries", t._uuid); deleted++; }
      catch (e: any) { console.error("[mtc] delete failed", e); }
    }

    if (newHrs > 0) {
      const dateStr = `${s.year}-${String(s.month).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
      // Use the form's default task for new days; carry the existing task if we're
      // just changing the hours on a day that already had one.
      const carriedTask = dayExisting[0] ? String(dayExisting[0]["Task"] || "") : "";
      const taskToUse = s.task.trim() || carriedTask;
      try {
        await DB.add("timeentries", {
          "Employee ID": s.empId,
          "Employee":    emp["Name"] || "",
          "Date":        dateStr,
          "Hours":       newHrs,
          "Task":        taskToUse,
          "Paid":        false,
          "Paid Date":   "",
          "Expense Ref": "",
          "Approved":    true, // owner adjusted/confirmed this day
          "Entered By":  "owner",
        });
        if (dayExisting.length > 0) replaced++; else added++;
      } catch (e: any) {
        toast("Save failed: " + (e?.message || e), "err");
        return;
      }
    }
  }

  const parts: string[] = [];
  if (added)    parts.push(`${added} day${added === 1 ? "" : "s"} added`);
  if (replaced) parts.push(`${replaced} updated`);
  if (deleted && !added && !replaced) parts.push(`${deleted} cleared`);
  if (skipped)  parts.push(`${skipped} paid day${skipped === 1 ? "" : "s"} skipped`);
  toast(parts.length ? `Time card saved: ${parts.join(", ")}` : "No changes to save");
  closeModal();
  _mtcState = null;
  render();
}

/* ============================================================
   Per-month payment - pay just one month from a time card row
   ============================================================ */
export function recordMonthlyPayment(empId: string, year: number, month: number): void {
  const emp = empById(empId);
  if (!emp) { toast("Employee not found", "err"); return; }
  const prefix = `${year}-${String(month).padStart(2, "0")}`;
  const unpaid = DB.table("timeentries").filter(t =>
    String(t["Employee ID"] || "") === empId &&
    String(t["Date"] || "").startsWith(prefix) &&
    !t["Paid"] && isApproved(t)
  );
  if (!unpaid.length) { toast("No unpaid hours for that month.", "err"); return; }
  const rate = Number(emp["Pay Rate"]) || 0;
  if (rate <= 0) { toast("Set this employee's pay rate first.", "err"); return; }

  const hours = unpaid.reduce((a, t) => a + (Number(t["Hours"]) || 0), 0);
  const gross = +(hours * rate).toFixed(2);
  const monthLabel = new Date(year, month - 1, 1).toLocaleDateString(undefined, { month: "long", year: "numeric" });

  const body = `
    <div class="space-y-3">
      <div class="panel-2 p-3 text-sm">
        Paying <b>${escapeHtml(emp["Name"] || "")}</b> for <b>${escapeHtml(monthLabel)}</b>:
        <b>${fmtN(hours)}</b> unpaid hour${hours === 1 ? "" : "s"} at <b>${fmt$(rate)}/hr</b>.
        <div class="mt-2 text-lg font-bold text-emerald-300">Gross pay: ${fmt$(gross)}</div>
      </div>
      <form id="payMonthForm" class="grid grid-cols-1 sm:grid-cols-2 gap-3" onsubmit="return false;">
        ${formField({ label: "Payment date", name: "Pay Date", type: "date", value: todayISO(), required: true })}
        ${formField({ label: "Payment method", name: "Pay Method", value: "Cash", options: PAY_METHODS })}
      </form>
      <div class="text-xs text-[var(--muted)]">Creates a deductible <b>Wages</b> expense for ${fmt$(gross)} tied to ${escapeHtml(monthLabel)} only - other months stay untouched.</div>
    </div>`;
  const footer = `
    <button class="btn btn-ghost" onclick="closeModal()">Cancel</button>
    <button class="btn btn-primary" onclick="confirmMonthlyPayment('${escapeHtml(empId)}', ${year}, ${month})">Record ${fmt$(gross)} payment</button>`;
  openModal({ title: `Pay ${monthLabel} Time Card`, body, footer });
}

export async function confirmMonthlyPayment(empId: string, year: number, month: number): Promise<void> {
  const emp = empById(empId);
  if (!emp) { toast("Employee not found", "err"); return; }
  const rate = Number(emp["Pay Rate"]) || 0;
  if (rate <= 0) { toast("Set this employee's pay rate first.", "err"); return; }
  const prefix = `${year}-${String(month).padStart(2, "0")}`;
  const unpaid = DB.table("timeentries").filter(t =>
    String(t["Employee ID"] || "") === empId &&
    String(t["Date"] || "").startsWith(prefix) &&
    !t["Paid"] && isApproved(t)
  );
  const hours = unpaid.reduce((a, t) => a + (Number(t["Hours"]) || 0), 0);
  const gross = +(hours * rate).toFixed(2);
  if (gross <= 0) { toast("Nothing to pay.", "err"); return; }

  const d = readForm($("#payMonthForm"));
  const payDate = d["Pay Date"] || todayISO();
  const method = d["Pay Method"] || "Cash";
  const monthLabel = new Date(year, month - 1, 1).toLocaleDateString(undefined, { month: "short", year: "numeric" });
  const ym = `${year}${String(month).padStart(2, "0")}`;
  const payRef = `PAY-${String(new Date().getFullYear()).slice(2)}-${ym}-${String(Date.now()).slice(-5)}`;

  try {
    await DB.add("expenses", {
      "Date":           payDate,
      "Category":       "Wages",
      "Vendor":         emp["Name"] || emp["Employee ID"],
      "Amount":         gross,
      "Deductible":     "Yes",
      "Payment Method": method,
      "Notes":          `Wages: ${fmtN(hours)} hrs @ ${fmt$(rate)}/hr for ${emp["Name"]} (${monthLabel}) · ${payRef}`,
    });
    for (const t of unpaid) {
      if (!t._uuid) continue;
      await DB.updateByUuid("timeentries", t._uuid, { ...t, "Paid": true, "Paid Date": payDate, "Expense Ref": payRef });
    }
  } catch (e: any) { toast("Cloud sync failed: " + (e?.message || e), "err"); return; }

  toast(`Recorded ${fmt$(gross)} for ${emp["Name"]} (${monthLabel})`);
  closeModal();
  render();
}

/* ============================================================
   Reopen / Delete time cards
   ------------------------------------------------------------
   Reopen: undo the wages-expense payment for a month so the
   calendar unlocks for editing. We match the wages expense by
   the PAY-XX-XXXXXX reference stored in each entry's Expense Ref
   and embedded in the expense's Notes. If an expense covers
   entries outside the target month (paying multiple months in
   one batch), we warn the user that those entries get unmarked
   too - it's the only consistent answer once the backing expense
   is deleted.
   ============================================================ */

function _findWagesExpensesForRefs(refs: string[]): Row[] {
  if (!refs.length) return [];
  const refSet = new Set(refs.filter(Boolean));
  if (!refSet.size) return [];
  return DB.table("expenses").filter(e => {
    if (e["Category"] !== "Wages") return false;
    const notes = String(e["Notes"] || "");
    for (const r of refSet) { if (notes.includes(r)) return true; }
    return false;
  });
}

export async function reopenTimeCard(empId: string, year: number, month: number): Promise<void> {
  const prefix = `${year}-${String(month).padStart(2, "0")}`;
  const monthLabel = new Date(year, month - 1, 1).toLocaleDateString(undefined, { month: "long", year: "numeric" });
  const paidEntries = DB.table("timeentries").filter(t =>
    String(t["Employee ID"] || "") === empId &&
    String(t["Date"] || "").startsWith(prefix) &&
    !!t["Paid"]
  );
  if (!paidEntries.length) { toast("No paid hours in this month to reopen.", "err"); return; }

  const refs = Array.from(new Set(paidEntries.map(t => String(t["Expense Ref"] || "")).filter(Boolean)));

  // Every entry tied to those refs becomes unpaid (could span other months /
  // employees if multiple months were paid in one batch).
  const affected = DB.table("timeentries").filter(t =>
    !!t["Paid"] && refs.includes(String(t["Expense Ref"] || ""))
  );
  const otherMonth = affected.length - paidEntries.length;

  let msg = `Reopen the ${monthLabel} time card for editing?\n\n`;
  msg += `This will delete the linked Wages expense (${refs.join(", ") || "no reference"}) `;
  msg += `and mark ${paidEntries.length} paid hour-entr${paidEntries.length === 1 ? "y" : "ies"} as unpaid.`;
  if (otherMonth > 0) {
    msg += `\n\nHEADS UP: that wages expense also covers ${otherMonth} entr${otherMonth === 1 ? "y" : "ies"} ` +
           `outside this month. Reopening will unmark all of them too, so you'll need to re-record those payments after.`;
  }
  msg += `\n\nYou can re-record payment after editing.`;
  if (!confirm(msg)) return;

  // Delete the matching Wages expenses.
  let expDeleted = 0;
  for (const exp of _findWagesExpensesForRefs(refs)) {
    if (!exp._uuid) continue;
    try { await DB.removeByUuid("expenses", exp._uuid); expDeleted++; }
    catch (e: any) { console.error("[reopen] expense delete failed", e); }
  }

  // Mark every affected entry as unpaid.
  let entriesUnmarked = 0;
  for (const t of affected) {
    if (!t._uuid) continue;
    try {
      await DB.updateByUuid("timeentries", t._uuid, {
        ...t,
        "Paid":        false,
        "Paid Date":   "",
        "Expense Ref": "",
      });
      entriesUnmarked++;
    } catch (e: any) { console.error("[reopen] entry update failed", e); }
  }

  toast(`Reopened: ${entriesUnmarked} entr${entriesUnmarked === 1 ? "y" : "ies"} unmarked, ${expDeleted} wages expense${expDeleted === 1 ? "" : "s"} deleted.`);
  render();
}

export async function deleteTimeCard(empId: string, year: number, month: number): Promise<void> {
  const prefix = `${year}-${String(month).padStart(2, "0")}`;
  const monthLabel = new Date(year, month - 1, 1).toLocaleDateString(undefined, { month: "long", year: "numeric" });
  const entries = DB.table("timeentries").filter(t =>
    String(t["Employee ID"] || "") === empId &&
    String(t["Date"] || "").startsWith(prefix)
  );
  if (!entries.length) { toast("No entries for this month.", "err"); return; }

  const paidEntries = entries.filter(t => !!t["Paid"]);
  const hasPaid = paidEntries.length > 0;
  const refs = Array.from(new Set(paidEntries.map(t => String(t["Expense Ref"] || "")).filter(Boolean)));
  const affectedByExp = hasPaid
    ? DB.table("timeentries").filter(t => !!t["Paid"] && refs.includes(String(t["Expense Ref"] || "")))
    : [];
  const otherMonth = affectedByExp.length - paidEntries.length;

  let msg = `Delete the entire ${monthLabel} time card?\n\n`;
  msg += `This will permanently remove ${entries.length} hour-entr${entries.length === 1 ? "y" : "ies"}`;
  if (hasPaid) msg += ` and delete the linked Wages expense (${refs.join(", ") || "no reference"})`;
  msg += `.`;
  if (otherMonth > 0) {
    msg += `\n\nHEADS UP: that wages expense also covers ${otherMonth} entr${otherMonth === 1 ? "y" : "ies"} ` +
           `outside this month. Those entries will be marked unpaid (not deleted) - you'll need to re-record payment for them.`;
  }
  msg += `\n\nThis cannot be undone.`;
  if (!confirm(msg)) return;

  // Delete the matching Wages expenses first (if any).
  let expDeleted = 0;
  if (hasPaid) {
    for (const exp of _findWagesExpensesForRefs(refs)) {
      if (!exp._uuid) continue;
      try { await DB.removeByUuid("expenses", exp._uuid); expDeleted++; }
      catch (e: any) { console.error("[delete-card] expense delete failed", e); }
    }
    // Unmark "other month" entries that shared the expense (we don't delete those).
    for (const t of affectedByExp) {
      if (!t._uuid) continue;
      if (String(t["Employee ID"] || "") === empId && String(t["Date"] || "").startsWith(prefix)) continue;
      try {
        await DB.updateByUuid("timeentries", t._uuid, {
          ...t,
          "Paid":        false,
          "Paid Date":   "",
          "Expense Ref": "",
        });
      } catch (e: any) { console.error("[delete-card] other-month unmark", e); }
    }
  }

  // Delete every entry for the target month.
  let entriesDeleted = 0;
  for (const t of entries) {
    if (!t._uuid) continue;
    try { await DB.removeByUuid("timeentries", t._uuid); entriesDeleted++; }
    catch (e: any) { console.error("[delete-card] entry delete failed", e); }
  }

  toast(`Deleted: ${entriesDeleted} entr${entriesDeleted === 1 ? "y" : "ies"}` + (expDeleted ? `, ${expDeleted} wages expense${expDeleted === 1 ? "" : "s"}` : "") + `.`);
  render();
}

/* ============================================================
   Approval actions — employee-submitted entries (Approved=false)
   become payable only after the owner approves them here (or edits
   them, or overwrites the day in the monthly time card).
   ============================================================ */
export async function approveTimeEntry(uuid: string): Promise<void> {
  const t = entryByUuid(uuid);
  if (!t) { toast("Entry not found", "err"); return; }
  try { await DB.updateByUuid("timeentries", uuid, { ...t, "Approved": true }); }
  catch (e: any) { toast("Cloud sync failed: " + (e?.message || e), "err"); return; }
  toast(`Approved ${fmtN(t["Hours"])} hrs on ${fmtDate(t["Date"])}`);
  render();
}

export async function rejectTimeEntry(uuid: string): Promise<void> {
  const t = entryByUuid(uuid);
  if (!t) { toast("Entry not found", "err"); return; }
  if (!confirm(`Reject ${fmtN(t["Hours"])} hrs on ${fmtDate(t["Date"])} for ${t["Employee"] || t["Employee ID"]}?\n\nThe entry is deleted — the employee can resubmit that day from the portal.`)) return;
  try { await DB.removeByUuid("timeentries", uuid); }
  catch (e: any) { toast("Cloud delete failed: " + (e?.message || e), "err"); return; }
  toast("Rejected");
  render();
}

export async function approveAllPending(): Promise<void> {
  const pend = pendingEntries();
  if (!pend.length) { toast("Nothing pending.", "err"); return; }
  const hrs = sum(pend, t => t["Hours"]);
  if (!confirm(`Approve all ${pend.length} submitted entr${pend.length === 1 ? "y" : "ies"} (${fmtN(hrs)} hrs)?\n\nThey become payable hours.`)) return;
  let n = 0;
  for (const t of pend) {
    if (!t._uuid) continue;
    try { await DB.updateByUuid("timeentries", t._uuid, { ...t, "Approved": true }); n++; }
    catch (e: any) { console.error("[approveAll]", e); }
  }
  toast(`Approved ${n} entr${n === 1 ? "y" : "ies"}`);
  render();
}

// Inline-handler bridges
(window as any).editEmployee   = editEmployee;
(window as any).saveEmployee   = saveEmployee;
(window as any).deleteEmployee = deleteEmployee;
(window as any).logTime        = logTime;
(window as any).editTime       = editTime;
(window as any).saveTime       = saveTime;
(window as any).deleteTime     = deleteTime;
(window as any).recordPayment  = recordPayment;
(window as any).confirmPayment = confirmPayment;
(window as any).payToggleDay   = payToggleDay;
(window as any).payToggleGroup = payToggleGroup;
(window as any).paySelectAll   = paySelectAll;
(window as any).openMonthlyTimeCard = openMonthlyTimeCard;
(window as any).updateMtcDay        = updateMtcDay;
(window as any).updateMtcTask       = updateMtcTask;
(window as any).changeMtcEmp        = changeMtcEmp;
(window as any).navMtcMonth         = navMtcMonth;
(window as any).saveMonthlyTimeCard = saveMonthlyTimeCard;
(window as any).reopenTimeCard         = reopenTimeCard;
(window as any).deleteTimeCard         = deleteTimeCard;
(window as any).recordMonthlyPayment   = recordMonthlyPayment;
(window as any).confirmMonthlyPayment  = confirmMonthlyPayment;
(window as any).approveTimeEntry       = approveTimeEntry;
(window as any).rejectTimeEntry        = rejectTimeEntry;
(window as any).approveAllPending      = approveAllPending;
